<script>
// Fibonacci Extension (Trend-Based) tool
import { Overlay, Tool } from 'trading-vue-js'
import Pin from './primitives/pin.js'
import Seg from './primitives/seg.js'
import { mapGetters, mapActions } from 'vuex'
import ToolSavingMixins from 'Tools/ToolSavingMixins'
import { convertToRGBA } from '../../utils/colors.js'
import { fibonacciLines } from '../../extensions/toolSettings/fibonacciLines.js'
import { debounce } from "../../utils/helper";
import math from './primitives/math.js'

export default {
  name: 'FibonacciExtension',
  mixins: [Overlay, Tool, ToolSavingMixins],
  methods: {
    meta_info() {
      return { author: 'ProTrader', version: '1.0.0' }
    },
    tool() {
      let prevSettings = localStorage.getItem('FibonacciExtension')
      if (prevSettings) {
        prevSettings = JSON.parse(prevSettings)
      }
      const defaultColor = prevSettings?.color || '#23a776'
      const defaultLineWidth = prevSettings?.lineWidth || 1
      const defaultLineStyle = prevSettings?.lineStyle || 'solid'
      const defaultLog = prevSettings?.logSetting || false
      return {
        group: 'Fibonacci',
        type: 'Segment',
        hint: 'Click three times to set Trend Start, Trend End, and Retracement End',
        data: [],
        settings: {
          color: defaultColor,
          lineWidth: defaultLineWidth,
          lineStyle: defaultLineStyle,
          fibSettings: JSON.parse(JSON.stringify(fibonacciLines)),
          logSetting: defaultLog,
          undo: 1,
          settingsList: ['color', 'lineWidth', 'lineStyle', 'fibSettings', 'settings'],
        },
      }
    },
    init() {
      const settings = this.$props.settings
      const isFinished = settings.$state === 'finished'

      // First pin: Trend Start
      this.pins.push(new Pin(this, 'p1'))

      // Second pin: Trend End
      // If finished, it's settled. If p3 exists, it means p2 already settled once.
      this.pins.push(
        new Pin(this, 'p2', {
          state: isFinished || settings.p3 ? 'settled' : 'tracking',
        })
      )

      // Third pin: Retracement End
      // If finished, it's settled and visible. 
      // If not finished but settings.p3 exists, it's tracking and visible.
      this.pins.push(
        new Pin(this, 'p3', {
          state: isFinished ? 'settled' : (settings.p3 ? 'tracking' : 'settled'),
          hidden: !isFinished && !settings.p3
        })
      )

      this.pins[1].on('settled', () => {
        // Only trigger the P3 tracking phase if P3 is still hidden (initial drawing)
        if (this.pins[2].hidden) {
          // After p2 is settled, show p3 and start tracking
          this.pins[2].hidden = false
          this.pins[2].state = 'tracking'
          // Initialize p3 at p2 position for better UX
          this.pins[2].t = this.pins[1].t
          this.pins[2].y$ = this.pins[1].y$
          this.pins[2].update() // Emit the initial position
        }
      })

      this.pins[2].on('settled', () => {
        // After p3 is settled, the tool is finished
        this.updateSettingsState(this)
        this.set_state('finished')
        this.$emit('drawing-mode-off')
        this.$emit('object-selected')
      })
      this.registerMouseEvent(this)
    },
    draw(ctx) {
      if (!this.p1 || !this.p2) return
      
      const layout = this.$props.layout
      ctx.lineWidth = this.line_width
      
      //--- Trend Line (P1 to P2) - Dashed Grey
      ctx.strokeStyle = '#787b86'
      ctx.setLineDash([5, 5])
      
      const x1 = layout.t2screen(this.p1[0])
      const y1_p = layout.$2screen(this.p1[1])
      const x2 = layout.t2screen(this.p2[0])
      const y2_p = layout.$2screen(this.p2[1])
      
      ctx.beginPath()
      ctx.moveTo(x1, y1_p)
      ctx.lineTo(x2, y2_p)
      ctx.stroke()

      // Add collision for Trend Line
      this.collisions.push((x, y) => {
        const tolerance = this.$props.config.TOOL_COLL || 5
        return math.point2seg([x, y], [x1, y1_p], [x2, y2_p]) < tolerance
      })

      // If P3 is not yet placed or hidden, we just draw the Trend Line
      if (!this.p3 || this.pins[2].hidden) {
        ctx.setLineDash([])
        this.render_pins(ctx)
        return
      }

      //--- Retracement Line (P2 to P3) - Dashed Grey
      const x3 = layout.t2screen(this.p3[0])
      const y3_p = layout.$2screen(this.p3[1])
      
      ctx.beginPath()
      ctx.moveTo(x2, y2_p)
      ctx.lineTo(x3, y3_p)
      ctx.stroke()

      // Add collision for Retracement Line
      this.collisions.push((x, y) => {
        const tolerance = this.$props.config.TOOL_COLL || 5
        return math.point2seg([x, y], [x2, y2_p], [x3, y3_p]) < tolerance
      })
      
      // Add collision for Fibonacci Level Lines
      const activeLevels = this.fibSettings
        .filter(m => m.enable)
        .sort((a, b) => a.val - b.val)

      const price1 = this.p1[1]
      const price2 = this.p2[1]
      const price3 = this.p3[1]
      const delta = price2 - price1
      
      const xStartScreen = x2
      const xEndScreen = x3

      ctx.setLineDash([])
      
      // Start filling from the base level (Ratio 0 at P3)
      let prevY = y3_p

      // Draw 0% level line
      ctx.strokeStyle = '#787b86'
      ctx.beginPath()
      ctx.moveTo(xStartScreen, y3_p)
      ctx.lineTo(xEndScreen, y3_p)
      ctx.stroke()

      // Add collision for 0% line
      this.collisions.push((x, y) => {
        const tolerance = this.$props.config.TOOL_COLL || 5
        return math.point2seg([x, y], [xStartScreen, y3_p], [xEndScreen, y3_p]) < tolerance
      })

      for (let i = 0; i < activeLevels.length; i++) {
        const m = activeLevels[i]
        
        let levelPrice;
        if (this.logScale && this.logSetting) {
          levelPrice = math.exp(math.log(price3) + (math.log(price2) - math.log(price1)) * m.val)
        } else {
          levelPrice = price3 + delta * m.val
        }
        
        const currentY = layout.$2screen(levelPrice)

        // Fill background between levels
        ctx.fillStyle = convertToRGBA(m.color, 0.15)
        ctx.fillRect(xStartScreen, prevY, xEndScreen - xStartScreen, currentY - prevY)
        prevY = currentY

        // Draw extension line
        ctx.beginPath()
        ctx.lineWidth = this.line_width
        ctx.strokeStyle = m.color
        ctx.moveTo(xStartScreen, currentY)
        ctx.lineTo(xEndScreen, currentY)
        ctx.stroke()

        // Add collision for each level line
        this.collisions.push((lx, ly) => {
          const tolerance = this.$props.config.TOOL_COLL || 5
          return math.point2seg([lx, ly], [xStartScreen, currentY], [xEndScreen, currentY]) < tolerance
        })

        // Draw Label
        ctx.fillStyle = m.color
        ctx.font = '12px Arial'
        ctx.textAlign = 'left'
        ctx.textBaseline = 'bottom'
        const label = `${m.val} (${levelPrice.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })})`
        ctx.fillText(label, xStartScreen + 4, currentY - 2)
      }

      ctx.setLineDash([])
      this.render_pins(ctx)
    },
    drag(p) {
        // Move all pins together when the tool is dragged
        this.pins.forEach(pin => {
            pin.t += p[0]
            pin.y$ += p[1]
            pin.update()
        })
    },
    render_pins(ctx) {
        if (this.pins) {
            const layout = this.$props.layout
            for (var p of this.pins) {
                if (p.hidden) continue
                
                // Recalculate screen coordinates from time/price to ensure they "stick" to the chart
                p.x = layout.t2screen(p.t)
                p.y = layout.$2screen(p.y$)

                ctx.beginPath()
                ctx.arc(p.x, p.y, 4.5, 0, Math.PI * 2)
                ctx.fillStyle = '#fff'
                ctx.fill()
                ctx.strokeStyle = '#f58002'
                ctx.lineWidth = 1.5
                ctx.stroke()
            }
        }
    },
    use_for() {
      return ['FibonacciExtension']
    },
    data_colors() {
      return [this.color, this.fontColor]
    },
    ...mapActions('action', ['saveUndoAction'])
  },
  computed: {
    ...mapGetters([
      'themePrimaryColor',
      'themeSecondaryColor',
      'themeFontColor',
      'dc',
      'logScale'
    ]),
    ...mapGetters('action', [
      'currentUndoValue',
      'currentRef'
    ]),
    undo() {
      return this.$props.settings.undo
    },
    sett() {
      return this.$props.settings
    },
    p1() {
      return this.$props.settings.p1
    },
    p2() {
      return this.$props.settings.p2
    },
    p3() {
      return this.$props.settings.p3
    },
    line_width() {
      return this.sett.lineWidth || 0.9
    },
    color() {
      return this.sett.color || '#42b28a'
    },
    fibSettings() {
      return this.sett.fibSettings || fibonacciLines
    },
    logSetting() {
      return this.sett.logSetting || false
    },
    fontColor() {
      return this.sett.fontColor || '#40b28a'
    },
  },
  watch: {
    undo(newVal, oldVal) {
      if (newVal != oldVal) {
        if (this.$props.settings?.$uuid == this.currentRef?.drawingRef) {
          this.pins.forEach((x, i) => x.update_from(
            this.currentUndoValue[x.name], true
          ))
          this.$nextTick(debounce(() => {
            this.saveUndoAction({
              chartIndex: this.currentRef?.chartIndex,
              val: this.currentRef?.drawingRef,
              data: this.currentUndoValue
            })
          }))
        }
      }
    }
  },
  created() {
    this.updateSettingsState(this)
  },
}
</script>
